/**
 * Runtime config: the shape of config/generated/game-config.json, plus
 * interning so the sim addresses definitions by integer index and never
 * by string.
 */
import { TRUE_BOSS_SCALE, BEAST_SCALE, RADIUS_PER_SCALE } from './constants.js';

/**
 * Floor for `Rules.spawnAhead`, in world units ahead of the player.
 *
 * MIRROR (see step.ts's own MIRROR_CAM_* comment for the same caveat): the
 * true source of truth is the live camera in apps/game/src/three/scene3d.ts
 * (CAM_HEIGHT 11.0 / CAM_BACK 11.0 / CAM_LOOKAT_FORWARD 4.7 / FOV 50) and
 * step.ts's own screenYForDepth, which project a world depth to screen01.y
 * under that pose. Under those constants, screenY crosses 0 (the top frame
 * edge) at depth ~54.6 -- anything shallower spawns the horde already
 * inside the visible frame instead of behind it. 60 keeps ~5.4 units of
 * margin over that analytic line, above the empirically-verified isle-12
 * value (54, real 560x940 captures, commit 2753f20) rather than sitting
 * exactly on it. If the camera constants above move, re-derive this value
 * against them the same way that commit did -- do not just nudge it.
 */
const MIN_SPAWN_DEPTH_OFFSCREEN = 60;

export type GateOp = 'add' | 'subtract' | 'multiply' | 'armory' | 'barrier' | 'armscrate' | 'vehicle' | 'ally' | 'armour';
export type StatName =
  | 'move_speed' | 'damage_per_hit' | 'fire_rate'
  | 'projectiles_per_shot' | 'pierce' | 'max_hp' | 'pickup_radius';
export type StatOp = 'add' | 'mul_add' | 'mul';
export type WeaponPattern = 'forward' | 'spread_3';
export type SpawnPattern = 'line_ahead' | 'arc_ahead' | 'flank_pair' | 'surround' | 'boss_center' | 'corridor_mass' | 'dual_column' | 'full_block';

export interface CharacterDef {
  id: string; name: string; tagline: string;
  /** Team colour the renderer paints the squad with. */
  tint: number;
  damageMult: number; fireRateMult: number; moveMult: number;
  squadHpMult: number; extraStreams: number;
}

export interface EnemyDef {
  id: string; name: string; hp: number; speed: number; contactDamage: number;
  radius: number; attackType: 'contact' | 'ranged'; attackRange: number;
  attackCooldownTicks: number; xpValue: number; spawnWeight: number;
  chapterMin: number; isBoss: boolean;
  /**
   * Mini-boss that arrives INSIDE the horde: authored at full strength,
   * immune to knockback, holds the line rather than breaching. Distinct
   * from isBoss, which is the chapter finale and ends the run on contact.
   */
  isBeast: boolean;
}
export interface WeaponDef {
  id: string; name: string; damagePerHit: number; projectilesPerShot: number;
  cooldownTicks: number; projectileSpeed: number; projectileLifetimeTicks: number;
  pierce: number; aoeRadius: number; knockback: number; pattern: WeaponPattern;
  pickupTier: number; derivedDps: number;
  /** Which visual identity the renderer gives this gun. Presentation only. */
  vfxProfile: string;
}
export interface GateDef {
  id: string; chapter: number; atTick: number; groupId: string;
  laneIndex: number; laneCount: number; op: GateOp; value: number;
  /**
   * Armour in DAMAGE POINTS for barrier/armscrate/vehicle, 0 otherwise.
   * Split from `value` in v3.1: one column carried both armour and payout,
   * in bodies-equivalent units, so a 40-point crate died to a single
   * 52-damage bullet and a 131-point barrier paid 131/500 = 0 bodies.
   */
  armor: number; label: string;
  /** THE DIAL (founder, 2026-08-22): per-lane authored increment a
   * projectile hit adds to that lane's live `gValue` for add/subtract/
   * multiply lanes (step.ts section 13b). 0 = inert, current behaviour
   * exactly -- every chapter but 12 authors 0 here. */
  pumpPerHit: number;
}
/** One monolith the level raises: when, which lane, how big, what's inside. */
export interface MonolithDef {
  chapter: number; atTick: number; lane: number; hp: number;
  titan: number; lifeSec: number;
  /**
   * What breaking the block PAYS (founder, 2026-08-14: the reward lane is
   * standing blocks shot down with real effort, not gates that drift
   * past): 'titan' | 'bodies' | 'crate' | 'armour' | 'vehicle'.
   */
  payload: string;
  /** Bodies gained / armour soldiers; 0 for crate/vehicle/titan. */
  value: number;
}
export interface UpgradeDef {
  id: string; name: string; stat: StatName; op: StatOp; value: number;
  rarityWeight: number; maxStacks: number; chapterMin: number;
}
export interface WaveDef {
  /** Forced lane for lane-locked patterns; -1 = derived from wave index. */
  lane: number;
  chapter: number; waveIndex: number; startTick: number; enemyId: string;
  count: number; spawnPattern: SpawnPattern; spawnDurationTicks: number;
}

export interface RawConfig {
  schemaVersion: number; configHash: string; tickRate: number;
  rules: Record<string, number>;
  enemies: EnemyDef[];
  characters: CharacterDef[]; weapons: WeaponDef[]; gates: GateDef[];
  upgrades: UpgradeDef[]; waves: WaveDef[]; monoliths: MonolithDef[];
  /**
   * Optional because `chapterList` below has always treated a missing
   * array as "no chapters" via a cast rather than a type error -- kept
   * optional to not force every existing caller to add one, but now a
   * real, checkable property instead of only living behind that cast.
   */
  chapters?: ChapterDef[];
  dirTable: number[]; xpTable: number[];
}

/**
 * One chapter's authored intent (docs/ISLE-1-SPEC.md, docs/ISLE-12-
 * SPEC.md): the crowd-ladder targets a level was designed against, and
 * (as of 2026-08-22) the actual per-chapter variant-B star bands. A
 * chapter with no row here isn't unauthored in the c_start/c_star/k/
 * beats/target_duration_sec sense (those are audit-only, read by
 * scripts/verify-level.ts, not by the runtime) -- only tThreshold/star2/
 * star3 are runtime-consumed, and only they have a defined fallback.
 */
export interface LevelDef {
  chapter: number; cStart: number; cStar: number; k: number; squadCap: number;
  tThreshold: number; star2: number; star3: number; beats: number; targetDurationSec: number;
}

/** One playable level: the difficulty axes the founder named. */
export interface ChapterDef {
  chapter: number; name: string;
  /**
   * How many lanes this isle runs. Two is a binary choice; three adds a
   * middle option and lets an isle seal one and play as a tighter two.
   * Varying it per isle is the cheapest variety in the genre -- the same
   * systems read as a different level because the geometry of the decision
   * changed.
   */
  laneCount: number;
  flowRamp: number; flowMax: number;
  enemyHpMult: number; bossHpMult: number;
  speedRamp: number; weaponCap: number; weaponStart: number;
  /**
   * Per-chapter override of weapons.csv row 0's damagePerHit, 0 = no
   * override. weapons.csv is shared across every chapter that starts on
   * weapon 0 (isles 1-3) -- this exists so a chapter-specific opening-
   * weakness rebalance cannot silently move another chapter's damage too.
   */
  weapon0DamageOverride?: number;
  /**
   * DENSITY REBALANCE (A-52 follow-up, 2026-09-01): per-chapter multiplier
   * on weapons.csv row 0's cooldownTicks, 0/undefined = no override (1x).
   * Same isolation reasoning as weapon0DamageOverride -- weapons.csv is
   * shared across isles 1-3. Diagnosed via `_diag-opening-fire.ts`: at
   * squad=1, the opening soldier's per-hit damage already massively
   * overkills a rift_skitterer (60 dmg vs 5 hp) and pierce is
   * damage-independent (each pierce level costs one more enemy hit
   * regardless of damage), so neither damage nor reach (both tried,
   * measured zero effect) touch the actual bottleneck: raw fire RATE
   * against a 36-enemy prefill glut, once isle 1's hordeSpeedMult was
   * raised for on-screen density. A cooldown multiplier < 1 raises rounds/
   * sec directly, the one lever that was still untried and load-bearing.
   */
  weapon0CooldownMult?: number;
  /**
   * Corridor depth override, 0 = use the shared spawn_ahead_units rule.
   * Depth IS density: the corridor must be deeper than weapon reach or the
   * curtain of fire sits on the spawn line and no mass ever advances —
   * but deeper is not monotonically better (probed 2026-08-13: 110 units
   * measured 0% winnable on isle 1; 90 measured 56%).
   */
  depth?: number;
  /**
   * Seconds of conveyor output pre-spawned across the corridor at t=0.
   * The mechanism behind "the lane is FULL from second one" — without it
   * the opening street is empty for the first ~10 seconds of conveyor
   * ramp-up, which is the only opening most players ever see.
   */
  prefillSec?: number;
  /**
   * Conveyor hot-lane share override, 0 = use the shared flow_hot_share
   * rule. The cold lane's share is what stands in the street while a
   * vehicle or ally wipes the hot one.
   */
  hotShare?: number;
  /**
   * 1 = the conveyor holds its fire while a question (a gate pair inside
   * the commit window) is on the table. Beat-orchestrated isles only.
   */
  qMute?: number;
  /** Extra u/s of gate approach on top of scroll. Beat isles only. */
  gateSpeed?: number;
  /** FORGE lane: rewards only, no enemy ever. -1 = none. */
  rewardLane?: number;
  /**
   * F4 AUDIT (2026-08-26): per-chapter multiplier on `eIsBoss`-flagged
   * movement speed (the chapter's own true boss AND any beast it spawns),
   * 0/undefined = no override (1x, `enemies.csv`'s own `speed_units_sec`).
   * `speed_units_sec` is a single row shared by every chapter that spawns
   * that enemy (stormhulk is every chapter's finale; gale_colossus is a
   * mid-run beast in isles 1-11) -- this exists so a chapter whose own
   * corridor depth cannot fit the stock transit time (measured: stormhulk
   * at 1.35u/s from spawn to its melee stop distance took 66s against a
   * run ending near 60) can close it without moving every OTHER chapter's
   * boss/beast pacing too.
   */
  bossSpeedMult?: number;
  /**
   * FIGHT SHAPE AUDIT (founder finding, 2026-08-27): per-chapter override
   * of how many soldiers ONE true-boss contact swing kills (the true boss
   * only -- never a mid-run beast, a different and already-tuned threat).
   * 0/undefined = no override (1, every chapter's behaviour before this
   * field existed). Swept against measured squad DPS: at 1-per-swing the
   * fight has no stable middle -- the boss either dies to ranged fire
   * before it ever reaches contact, or it survives long enough that the
   * squad-shrinks-then-DPS-drops spiral wipes the whole squad almost every
   * time (isle 1, reference squad: 7/7 wins with an untouched squad at
   * boss_hp_mult 10, 2/7 wins with most squads at 0 by mult 13). A harder,
   * faster-resolving swing needs far less boss HP to feel dangerous, which
   * shortens the melee window and gives ranged DPS a real chance to still
   * matter during it instead of the fight being decided entirely by
   * whichever side reaches its cooldown-gated attrition curve first.
   */
  bossSwingKillCount?: number;
  /**
   * VISIBLE-KILL AUDIT (founder finding, 2026-08-27): per-chapter cap on
   * weapon reach, 0/undefined = no override (falls back to the shared
   * weapon_range_ceiling sanity cap, effectively unbounded). The shared
   * REACH-AXIS FIX sized every weapon's base reach at ~75 units against a
   * 90-unit spawn depth -- fine at the time, but isle 1 and isle 12's
   * spawn depth is now floored at 60 (MIN_SPAWN_DEPTH_OFFSCREEN, the
   * off-screen-spawn fix), so a squad-1 tier-1 weapon already outranges
   * the spawn line by 15 units: every enemy this chapter spawns could be
   * killed before it ever became visible, which is a second, independent
   * system quietly defeating the whole point of spawning off-screen.
   */
  weaponReachCap?: number;
  /**
   * VISIBLE-KILL AUDIT, PART 2 (founder finding, 2026-08-27): per-chapter
   * override of the monolith spawn distance, as a fraction of spawnAhead
   * (0/undefined = no override, the shared 0.55). Measured directly on
   * isle 12: with the shared fraction, a monolith's distance from the
   * player stayed locked in [31.6, 33] units for its ENTIRE life -- it is
   * always destroyed (or replaced by the next one) before it drifts
   * meaningfully closer, so the reveal always happens far away and small,
   * never up close. No amount of hologram/material tuning fixes a
   * positioning problem. `step.ts` reads this SAME fraction for both the
   * spawn position and the reward-value time-decay formula's own distance
   * denominator, so changing it cannot silently miscalibrate the reward
   * math -- the two stay derived from one number.
   */
  monolithSpawnFrac?: number;
  /**
   * KILL-POWER AUDIT: per-chapter override of an authored beast's HP
   * multiplier, decoupled from `enemyHpMult` (which also scales ordinary
   * fodder). 0/undefined = no override, fall back to `enemyHpMult` (every
   * chapter's behaviour before this field existed).
   */
  beastHpMult?: number;
  /**
   * PACE AUDIT: per-chapter multiplier on ordinary fodder's base movement
   * speed (HORDE_SPEED), 0/undefined = no override (1x). The per-wave-
   * cleared difficultyTier ramp still applies on top of this, so the
   * chapter still escalates -- it starts slower, not flat-slow forever.
   */
  hordeSpeedMult?: number;
  /**
   * SURVIVAL AUDIT: per-chapter multiplier on breach damage, 0/undefined
   * = no override (1x). See Rules.breachCostMult's own comment.
   */
  breachCostMult?: number;
  /**
   * DENSITY REBALANCE (A-52 follow-up, 2026-09-01): banked shield at run
   * start, in units of squadUnitHp, 0/undefined = no override (none,
   * every chapter's behaviour before this field existed). Isle 1's
   * hordeSpeedMult was raised 1->4 to fix on-screen horde density
   * (founder directive: fix density first, rebalance survivability
   * separately) -- squad_start=1 (deliberately preserved, "the crowd
   * multiplying from 1 IS the hook") now dies to the prefill's nearest
   * bodies before it can react. `damageSquad`'s ARMOUR FIRST rule
   * (step.ts) already soaks any amount before touching squadHpPool, so a
   * banked shield buys real reaction time against the ONE FOR ONE contact
   * rule (also deliberately preserved) without touching squad_start,
   * squad_max, or the contact-damage design at all.
   */
  startingShieldMult?: number;
}

export interface Rules {
  arenaWidth: number; arenaHeight: number; scrollSpeed: number;
  squadStart: number; squadMax: number; squadUnitHp: number; moveSpeed: number;
  pickupRadius: number; magnetSpeed: number; projectilesPerSquadStep: number;
  maxProjectilesPerVolley: number; maxLevel: number;
  enemyPoolCap: number; projectilePoolCap: number; enemyProjectilePoolCap: number;
  pickupPoolCap: number; gatePoolCap: number;
  spawnAhead: number; playerHitRadius: number; levelUpOffers: number; laneCount: number;
  /**
   * C1: depth between ranks of an AUTHORED WAVE.
   *
   * A wave used to spawn every body on one line at `spawnAhead`, so the horde
   * was a flat curtain one rank thick — measured at t30 as 286 of 298 bodies
   * inside a single 10-unit band. Bodies that share a spawn line and a speed
   * never separate, so no amount of render work could give the mass depth it
   * did not have in the sim.
   *
   * Deliberately larger than the conveyor's rank gap: the conveyor spawns
   * CONTINUOUSLY, so time spreads its bodies through the corridor for free.
   * A discrete wave has no such spread and must carry its own.
   */
  waveRankGap: number;
  difficultyRamp: number;
  difficultyPerSquad: number;
  /** Finger travel to corridor travel. 1.0 is 1:1 across the screen. */
  dragGain: number;
  /** Units over which the squad decelerates into the finger's target. */
  followSoftZone: number;
  /** Scroll acceleration per wave cleared. Pace IS a difficulty axis. */
  speedRampPerWave: number;
  /** Ceiling on that acceleration. */
  speedRampMax: number;
  /**
   * Gates spawn CLOSER than enemies. Sharing the enemy spawn distance
   * meant a gate authored at t=2s did not reach the player until t=9.3s,
   * so a squad that starts at 1 was dead before its first multiplier.
   */
  gateSpawnAhead: number;
  /**
   * Odds a kill drops a recruit. The squad has to grow by FIGHTING; when
   * gates were the only source, the game was a booster-picker with combat
   * as scenery, and cutting the gates down simply killed every bot policy
   * inside a minute.
   */
  recruitDropChance: number;
  /**
   * Kills alone can build a squad this big (the opening fireteam); past it
   * recruit drops only REFILL toward squadPeak, and growth belongs to the
   * reward lane. The flat drop chance without this ceiling let the horde
   * out-recruit the rewards at high kill volumes (greedy won 19% of isle 1).
   */
  recruitGrowthFloor: number;
  /** Recruits a boss is worth. */
  recruitDropBoss: number;
  /**
   * What a leaked body costs, as a SHARE of the current squad.
   *
   * It used to be a flat 0.12 of one soldier's hp -- 1.2 damage. A
   * 240-strong squad holds 2400hp, so killing it by leaking would take
   * 2000 bodies and the entire chapter only authors 573. Ignoring the
   * horde was not merely cheap, it was arithmetically impossible to lose
   * by, which is exactly why the game had no challenge.
   */
  breachFractionOfSquad: number;
  /** Floor in bodies, so a leak still stings when the squad is tiny. */
  breachMinBodies: number;
  /**
   * SURVIVAL AUDIT (2026-08-26): per-chapter multiplier on breach damage,
   * 1 everywhere a chapter doesn't override it. `breach_min_bodies` (0.05)
   * is "twenty leaks cost one soldier" -- fine for a squad of 20+, where
   * losing one is a rounding error, but isle 1 is locked at squad=1 for
   * its ENTIRE early game (no positive squad growth exists yet at that
   * point) and its own fodder density puts ~20 leaks on the field inside
   * 8 seconds -- measured directly in the real client: squadHpPool
   * 180->0 in under 8s from breach damage alone, no gate or boss
   * involved. The isle's OWN density is what breachMinBodies assumed a
   * big squad would absorb; a squad of exactly one has nothing to absorb
   * it with.
   */
  breachCostMult: number;
  /** XP per body earned past squadMax, so the cap is never a dead end. */
  overflowXpPerBody: number;
  /** Base odds a kill drops a gun; scaled up when behind the weapon ladder. */
  weaponDropChance: number;
  /** How many chapters the weapon library is meant to span. */
  chapterCount: number;
  /** Fraction of the direct hit dealt to enemies inside aoe_radius. */
  aoeSplashFraction: number;
  /** Baseline bodies/sec from the continuous flow spawner at t=0. */
  flowStartPerSec: number;
  /** Bodies/sec added per second — the flow accelerates. */
  flowRampPerSec2: number;
  /** Flow ceiling in bodies/sec. */
  flowMaxPerSec: number;
  /** Barrier payout: bodies gained = barrier hp / this. */
  barrierPayoutDiv: number;
  /**
   * BLOCK_RACE reward dampener (step.ts breakBlock): the raw formula
   * (remainingTicks/totalTicks * mValue) pays MORE the faster a monolith
   * dies, and a bigger squad kills faster, so it was a closed, unbounded
   * feedback loop -- bigger squad -> faster kill -> closer to full payout
   * -> bigger squad. Confirmed empirically (2026-08-29, AUDIT.md A-41):
   * squadPeak hit the 20-cap at a median of t=15s into a 45s isle-12 run,
   * and the economy had ZERO headroom left for a config-value reduction
   * (two attempts, 0/20 wins each). Applied as `1/(1+squadSize*this)` on
   * the reward -- explicitly taxes the payout by CURRENT squad size
   * before the kill, breaking the loop at the one point it closes through,
   * rather than reducing the reward for everyone regardless of squad size
   * (which is what the failed CSV attempts did, and why they collapsed
   * survivability instead of just slowing growth). 0 = the old unbounded
   * behavior, for a clean rollback path.
   */
  raceRewardDampenK: number;
  /**
   * A-43: the one added player verb. Seconds of full invulnerability
   * (`damageSquad` no-ops entirely) after a dodge command, and seconds
   * before another dodge can be triggered. Deliberately full invuln, not
   * a damage-reduction percentage — a partial dodge is not a verb the
   * player can read the outcome of; a clean dash-through is.
   */
  dodgeActiveSec: number;
  dodgeCooldownSec: number;
  /**
   * A-45. Seconds a level-up's choice window stays open before falling
   * back to the seeded random draw. Short by design -- long enough to
   * register one tap, short enough that a level-up spree still resolves
   * quickly and the run never feels like it's waiting on the player.
   */
  boonWindowSec: number;
  /** Seconds a claimed vehicle fights alongside the squad. */
  vehicleDurationSec: number;
  /** Damage multiplier while a vehicle is deployed. */
  vehicleDamageMult: number;
  /** Streams the vehicle adds on top of the squad's rifles. */
  vehicleExtraStreams: number;
  /**
   * How far the vehicle's own streams reach, in units. Bounded so a tank
   * clears the street in FRONT of itself rather than to the horizon —
   * unbounded, every vehicle window emptied the corridor below the
   * reference density floor.
   */
  vehicleReach: number;
  /**
   * ENGAGEMENT-DISTANCE FIX (framing pass, 2026-08-18): originally a
   * working 30-unit clamp -- 10 of 12 weapons outranged the 44-unit arena
   * outright (ratios 1.0-4.96, measured in docs/DECISIONS.md), so the
   * squad killed almost everything before it ever closed inside 30 units
   * of the player. Swept R x damage-multiplier headlessly before landing
   * there: R=30 with damagePerHit x2.5 held wave-5 beyond30 at 25.4%,
   * wave-10 at 27.4%, winrate 39/40 -- identical to the pre-change
   * baseline.
   *
   * REACH-AXIS FIX (founder, 2026-08-20): that clamp flattened 10 of 12
   * weapons to identical reach and left arc_blade/storm_hammer (the top
   * of the ladder) SHORTER than every earlier tier -- an inversion, not a
   * cap. `weapons.csv`'s `projectile_lifetime_sec` now authors a
   * monotonic tier1-12 reach ladder directly (~20-55 units); this is a
   * SANITY ceiling nothing on that ladder (plus `reachPerSquad`, below)
   * should ever reach, not a working constraint.
   */
  weaponRangeCeiling: number;
  /**
   * VISIBLE-KILL AUDIT: per-chapter cap on weapon reach, defaults to
   * `weaponRangeCeiling` (no additional constraint) when the chapter
   * doesn't override it. See ChapterDef.weaponReachCap.
   */
  weaponReachCap: number;
  /**
   * VISIBLE-KILL AUDIT PART 2: monolith spawn distance as a fraction of
   * spawnAhead, 0.55 everywhere the chapter doesn't override it. See
   * ChapterDef.monolithSpawnFrac.
   */
  monolithSpawnFrac: number;
  /** See ChapterDef.startingShieldMult's own comment. */
  startingShieldMult: number;
  /**
   * REACH-AXIS FIX (founder, 2026-08-20): squad size's own contribution
   * to effective reach, in world units per soldier past the first --
   * `step.ts`'s fire loop computes `baseWeaponReach + reachPerSquad *
   * (squadSize - 1)`, keyed to `squadSize` directly rather than to the
   * volley's projectile count (which the 90-projectile cap already
   * saturates for several weapons well before squad hits 20). Reach used
   * to be completely flat against squad size -- this is the axis that
   * was missing (BEAT_GRAMMAR-adjacent reach-audit, 2026-08-20).
   */
  reachPerSquad: number;
  /**
   * PHYSICS LAW (global CLAUDE.md, non-negotiable): no entity's centre may
   * come closer to a rail than this. Sized to the widest common body's
   * half-width, so shoulders never poke through a solid.
   */
  wallMargin: number;
  /**
   * T for variant B (THRESHOLD AT THE BOSS, founder spec 2026-08-21):
   * squadSize >= this at boss spawn wins, below it loses. Only
   * load-bearing when RunState.variant===1.
   *
   * Per-chapter when levels.csv has a row for this chapter (founder,
   * 2026-08-22 -- levels.csv was authored back on 2026-08-21 but never
   * actually wired past rules.csv's global value until now, so isle 1's
   * own t_threshold=9 row sat inert). Falls back to rules.csv's global
   * `clash_threshold_t` for any chapter with no row.
   */
  clashThresholdT: number;
  /** Star 2 threshold. Per-chapter (levels.csv `star2`), else 1.15x clashThresholdT. */
  star2Threshold: number;
  /** Star 3 threshold. Per-chapter (levels.csv `star3`), else 1.3x clashThresholdT. */
  star3Threshold: number;
  /** Share of the conveyor concentrated in the hot lane. */
  flowHotShare: number;
  /** Seconds a lane stays hot before the pressure switches sides. */
  flowLaneHoldSec: number;
  /**
   * Seconds of conveyor output pre-spawned across the corridor before the
   * first tick, so the street is already full when the run opens. Per
   * chapter (chapters.csv `prefill_sec`); 0 = the opening starts empty.
   */
  flowPrefillSec: number;
  /**
   * The conveyor holds its fire while a question is on the table — the
   * beat grammar's "one stimulus at a time", enforced where the bodies
   * come from. Per chapter (chapters.csv `q_mute`).
   */
  flowQuestionMute: boolean;
  /**
   * Extra units/sec a gate closes on top of the corridor scroll — the
   * beat grammar's fast-approaching question. 0 everywhere the old
   * schedule runs. Per chapter (chapters.csv `gate_speed`).
   */
  gateApproachSpeed: number;
  /**
   * F4 AUDIT: multiplier on `eIsBoss`-flagged (true boss + beast) movement
   * speed for this chapter. 1 everywhere the chapter doesn't override it.
   * See `ChapterDef.bossSpeedMult`.
   */
  bossSpeedMult: number;
  /**
   * FIGHT SHAPE AUDIT: how many soldiers ONE true-boss contact swing
   * kills, 1 everywhere the chapter doesn't override it. See
   * ChapterDef.bossSwingKillCount.
   */
  bossSwingKillCount: number;
  /**
   * PACE AUDIT (2026-08-26): multiplier on ordinary fodder's HORDE_SPEED
   * for this chapter, 1 everywhere the chapter doesn't override it. The
   * existing difficultyTier ramp still multiplies on top, so a chapter
   * that sets this below 1 still escalates over the run -- it just starts
   * from a slower floor. See `ChapterDef.hordeSpeedMult`.
   */
  hordeSpeedMult: number;
  /**
   * FORGE (founder mandate M-A): this lane carries REWARDS ONLY — no
   * enemy may ever spawn in or arrive from it. The prefill floods the
   * other lane. -1 = the isle has no forge lane.
   */
  rewardLane: number;
  /**
   * HORDE LANE PIN (founder mandate, 2026-08-27): -1 = no pin (chapters
   * that draw or split lanes across their own waves, unchanged). When
   * every authored wave in a chapter explicitly commits to the SAME lane
   * -- isles 1 and 12 both do, today -- that lane is the only one the
   * horde is ever meant to occupy. Derived once at load (see loadConfig),
   * never authored directly, so it can never drift out of sync with the
   * waves it describes.
   */
  hordeLane: number;
  /**
   * D13 crossover, same-lane branch: chance per evaluation a body already
   * authored into the player's current lane fully converges onto the
   * player's X once inside `CROSSOVER_RANGE_UNITS` (step.ts). Was the
   * hardcoded module constant `CROSSOVER_SHARE = 0.15` (step.ts, prior to
   * this config key) -- swept 1.0/0.75/0.5/0.35/0.25/0.15 against the
   * (then-stationary) reference policy, 0.15 the only value clearing the
   * 30% winrate floor (48%). Landed 2026-08-17, full record in
   * FRAME_SPEC.md §10/§11 and tasks/defects.md D13.
   */
  convergeChance: number;
  /**
   * D13 crossover, cross-lane branch: chance per evaluation a body
   * authored into a DIFFERENT lane than the player's current one still
   * converges onto the player's X. Default 0 -- before this key existed,
   * the same 0.15 (`CROSSOVER_SHARE`) applied regardless of lane, which
   * let any nearby body cross into a lane it was never authored into and,
   * on chapter 12's short/sparse waves, emptied every authored lane
   * assignment of meaning (isle-12 asset/mechanic report, this session).
   */
  crossLaneConvergeChance: number;
}

export interface GameConfig {
  readonly raw: RawConfig;
  /** Which level this config was loaded for. */
  readonly chapter: number;
  readonly chapterName: string;
  /** Highest weapon tier index this level hands out. */
  readonly weaponCap: number;
  /** Weapon tier this level STARTS you on — the kit you earned. */
  readonly weaponStart: number;
  readonly rules: Rules;
  readonly enemies: readonly EnemyDef[];
  readonly characters: readonly CharacterDef[];
  readonly weapons: readonly WeaponDef[];
  readonly upgrades: readonly UpgradeDef[];
  /** Monoliths for the loaded chapter, ordered by atTick. */
  readonly monoliths: readonly MonolithDef[];
  /** Waves for the loaded chapter, ordered by startTick. */
  readonly waves: readonly (WaveDef & { enemyIdx: number })[];
  /** Gate offers for the loaded chapter, grouped and ordered by atTick. */
  readonly gateGroups: readonly { atTick: number; lanes: readonly GateDef[] }[];
  readonly dirTable: Float64Array;
  readonly xpTable: Int32Array;
  readonly configHash: string;
}

const req = (rules: Record<string, number>, key: string): number => {
  const v = rules[key];
  if (v === undefined) throw new Error(`config: rules is missing "${key}"`);
  return v;
};

/** Every chapter's header row, for the level-select menu. */
export function chapterList(raw: RawConfig): ChapterDef[] {
  return (raw.chapters ?? []).slice().sort((a, b) => a.chapter - b.chapter);
}

export function loadConfig(raw: RawConfig, chapter: number): GameConfig {
  const enemyIdx = new Map(raw.enemies.map((e, i) => [e.id, i]));

  // The CHAPTER row overrides the shared rules. Difficulty across the
  // fifteen levels is a table, not fifteen forks in the logic: speed
  // (flow acceleration and ceiling), toughness (enemy and boss hp) and
  // complexity (how far up the weapon ladder the level reaches).
  const ch = chapterList(raw).find((c) => c.chapter === chapter)
    ?? chapterList(raw)[0];
  // Optional per-chapter row (levels.csv) -- absent for most chapters
  // today, which is exactly the case rules.csv's global clash_threshold_t
  // exists to cover.
  const level = (raw as unknown as { levels?: LevelDef[] }).levels
    ?.find((l) => l.chapter === chapter);

  const waves = raw.waves
    .filter((w) => w.chapter === chapter)
    .map((w) => {
      const idx = enemyIdx.get(w.enemyId);
      if (idx === undefined) throw new Error(`config: wave references unknown enemy "${w.enemyId}"`);
      return { ...w, enemyIdx: idx };
    })
    .sort((a, b) => a.startTick - b.startTick || a.waveIndex - b.waveIndex);

  // HORDE LANE PIN: see Rules.hordeLane's own comment. `w.lane` here is
  // the RAW authored column (-1 = drawn/derived per wave-index, never a
  // committed lane) -- only when every single wave row in the chapter
  // commits to the identical explicit lane is the chapter single-laned
  // by construction, and only then does the background conveyor (which
  // has no wave of its own to read a lane from) get pinned to match it.
  //
  // BOSS EXCLUDED (founder finding, 2026-09-02): `boss_center` picks its
  // OWN lane for its own reasons (step.ts's own comment: "killing it
  // means committing to that lane" -- a deliberate, independently-tuned
  // choice, not part of "the horde's lane"). Folding the boss's authored
  // lane into this all-waves check meant giving it the SAME lane as the
  // horde (to keep the pin alive) collapsed winrate 68%->30% -- right at
  // the M2 floor -- because it stacked boss engagement on top of horde
  // pressure in the identical lane. The pin only needs every HORDE wave
  // to agree; the boss's own lane is free to differ.
  const hordeWaves = waves.filter((w) => w.spawnPattern !== 'boss_center');
  const hordeLane = hordeWaves.length > 0 && hordeWaves[0]!.lane >= 0
    && hordeWaves.every((w) => w.lane === hordeWaves[0]!.lane)
    ? hordeWaves[0]!.lane : -1;

  const byGroup = new Map<string, GateDef[]>();
  for (const g of raw.gates) {
    if (g.chapter !== chapter) continue;
    const list = byGroup.get(g.groupId) ?? [];
    list.push(g);
    byGroup.set(g.groupId, list);
  }
  const gateGroups = [...byGroup.values()]
    .map((lanes) => ({
      atTick: lanes[0]!.atTick,
      lanes: [...lanes].sort((a, b) => a.laneIndex - b.laneIndex),
    }))
    // Explicit numeric comparator, and a stable tiebreak, so ordering never
    // depends on Map insertion history.
    .sort((a, b) => a.atTick - b.atTick || a.lanes[0]!.id.localeCompare(b.lanes[0]!.id));

  const r = raw.rules;
  const clashThresholdT = level?.tThreshold ?? req(r, 'clash_threshold_t');
  const star2Threshold = level?.star2 ?? clashThresholdT * 1.15;
  const star3Threshold = level?.star3 ?? clashThresholdT * 1.3;
  return {
    raw,
    chapter: ch?.chapter ?? chapter,
    chapterName: ch?.name ?? `Isle ${chapter}`,
    weaponCap: ch?.weaponCap ?? 1,
    weaponStart: ch?.weaponStart ?? 0,
    rules: {
      arenaWidth: req(r, 'arena_width'),
      arenaHeight: req(r, 'arena_height'),
      scrollSpeed: req(r, 'scroll_speed_units_sec'),
      squadStart: req(r, 'squad_start'),
      squadMax: req(r, 'squad_max'),
      squadUnitHp: req(r, 'squad_unit_hp'),
      moveSpeed: req(r, 'move_speed_units_sec'),
      pickupRadius: req(r, 'pickup_radius'),
      magnetSpeed: req(r, 'magnet_speed_units_sec'),
      projectilesPerSquadStep: req(r, 'projectiles_per_squad_step'),
      maxProjectilesPerVolley: req(r, 'max_projectiles_per_volley'),
      maxLevel: req(r, 'max_level'),
      enemyPoolCap: req(r, 'enemy_pool_cap'),
      projectilePoolCap: req(r, 'projectile_pool_cap'),
      enemyProjectilePoolCap: req(r, 'enemy_projectile_pool_cap'),
      pickupPoolCap: req(r, 'pickup_pool_cap'),
      gatePoolCap: req(r, 'gate_pool_cap'),
      // The CHAPTER's corridor depth wins over the shared rule (0 = no
      // override), mirroring laneCount below. PHYSICS LAW enforced at the
      // CHOKEPOINT (founder mandate, 2026-08-27): floored at
      // MIN_SPAWN_DEPTH_OFFSCREEN so no chapter's authored `depth` can ever
      // spawn the horde already inside the visible frame again -- isle 12
      // was fixed to depth 54 after being verified off-screen by real
      // 560x940 captures (commit 2753f20), then silently regressed to 32 in
      // the very next balance pass with no re-verification. A number in a
      // CSV cannot enforce a rule; this clamp can.
      spawnAhead: Math.max(
        MIN_SPAWN_DEPTH_OFFSCREEN,
        ch?.depth && ch.depth > 0 ? ch.depth : req(r, 'spawn_ahead_units'),
      ),
      waveRankGap: req(r, 'wave_rank_gap_units'),
      playerHitRadius: req(r, 'player_hit_radius'),
      levelUpOffers: req(r, 'level_up_offers'),
      // The CHAPTER's lane count wins over the shared rule.
      laneCount: ch?.laneCount && ch.laneCount > 0 ? ch.laneCount : req(r, 'lane_count'),
      difficultyRamp: req(r, 'difficulty_ramp_per_wave'),
      difficultyPerSquad: req(r, 'difficulty_per_squad_member'),
      dragGain: req(r, 'drag_gain'),
      followSoftZone: req(r, 'follow_soft_zone_units'),
      speedRampPerWave: ch?.speedRamp ?? req(r, 'speed_ramp_per_wave'),
      speedRampMax: req(r, 'speed_ramp_max'),
      gateSpawnAhead: req(r, 'gate_spawn_ahead_units'),
      recruitDropChance: req(r, 'recruit_drop_chance'),
      recruitGrowthFloor: req(r, 'recruit_growth_floor'),
      recruitDropBoss: req(r, 'recruit_drop_boss'),
      breachFractionOfSquad: req(r, 'breach_fraction_of_squad'),
      breachCostMult: ch?.breachCostMult && ch.breachCostMult > 0 ? ch.breachCostMult : 1,
      breachMinBodies: req(r, 'breach_min_bodies'),
      overflowXpPerBody: req(r, 'overflow_xp_per_body'),
      weaponDropChance: req(r, 'weapon_drop_chance'),
      chapterCount: req(r, 'chapter_count'),
      aoeSplashFraction: req(r, 'aoe_splash_fraction'),
      flowStartPerSec: req(r, 'flow_start_per_sec'),
      flowRampPerSec2: ch?.flowRamp ?? req(r, 'flow_ramp_per_sec2'),
      flowMaxPerSec: ch?.flowMax ?? req(r, 'flow_max_per_sec'),
      flowHotShare: ch?.hotShare && ch.hotShare > 0 ? ch.hotShare : req(r, 'flow_hot_share'),
      flowLaneHoldSec: req(r, 'flow_lane_hold_sec'),
      flowPrefillSec: ch?.prefillSec ?? 0,
      flowQuestionMute: (ch?.qMute ?? 0) > 0,
      gateApproachSpeed: ch?.gateSpeed ?? 0,
      bossSpeedMult: ch?.bossSpeedMult && ch.bossSpeedMult > 0 ? ch.bossSpeedMult : 1,
      bossSwingKillCount: ch?.bossSwingKillCount && ch.bossSwingKillCount > 0 ? ch.bossSwingKillCount : 1,
      hordeSpeedMult: ch?.hordeSpeedMult && ch.hordeSpeedMult > 0 ? ch.hordeSpeedMult : 1,
      rewardLane: ch?.rewardLane ?? -1,
      hordeLane,
      convergeChance: req(r, 'converge_chance'),
      crossLaneConvergeChance: req(r, 'cross_lane_converge_chance'),
      barrierPayoutDiv: req(r, 'barrier_payout_div'),
      raceRewardDampenK: r['race_reward_dampen_k'] ?? 0,
      dodgeActiveSec: req(r, 'dodge_active_sec'),
      dodgeCooldownSec: req(r, 'dodge_cooldown_sec'),
      boonWindowSec: req(r, 'boon_window_sec'),
      vehicleDurationSec: req(r, 'vehicle_duration_sec'),
      vehicleDamageMult: req(r, 'vehicle_damage_mult'),
      vehicleExtraStreams: req(r, 'vehicle_extra_streams'),
      vehicleReach: req(r, 'vehicle_reach_units'),
      weaponRangeCeiling: req(r, 'weapon_range_ceiling'),
      weaponReachCap: ch?.weaponReachCap && ch.weaponReachCap > 0 ? ch.weaponReachCap : req(r, 'weapon_range_ceiling'),
      monolithSpawnFrac: ch?.monolithSpawnFrac && ch.monolithSpawnFrac > 0 ? ch.monolithSpawnFrac : 0.55,
      startingShieldMult: ch?.startingShieldMult ?? 0,
      reachPerSquad: req(r, 'reach_per_squad'),
      wallMargin: req(r, 'wall_margin_units'),
      clashThresholdT, star2Threshold, star3Threshold,
    },
    enemies: raw.enemies.map((e) => ({
      ...e,
      // Beasts scale with the HORDE, not the finale: the boss column is a
      // knife-edge DPS check and grew 4-8x through tuning -- applied to the
      // three mid-run beasts it made isle 8 unwinnable at the first one.
      // KILL-POWER AUDIT (2026-08-26): a chapter that wants a beast's OWN
      // DPS-check curve, decoupled from that same chapter's ordinary fodder
      // HP, sets `beastHpMult` (0 = fall back to `enemyHpMult`, the
      // existing behaviour every other chapter keeps). Isle 1's
      // gale_colossus needed this: enemy_hp_mult there is tuned for
      // rift_skitterer's F1 death-depth, and reusing it for the beast would
      // have moved both together.
      hp: e.hp * (e.isBoss ? (ch?.bossHpMult ?? 1)
        : e.isBeast ? (ch?.beastHpMult && ch.beastHpMult > 0 ? ch.beastHpMult : (ch?.enemyHpMult ?? 1))
        : (ch?.enemyHpMult ?? 1)),
      // F2 AUDIT: the true boss and every authored beast get their CONTACT
      // radius from the same scale constant the renderer draws them at
      // (RADIUS_PER_SCALE, packages/run-engine/src/constants.ts) instead of
      // an independently authored enemies.csv value -- that's what let
      // stormhulk's limbs cross the squad while its hit-circle sat well
      // inside its silhouette. enemies.csv's `radius` column is ignored for
      // these two rows (kept numerically matching for readability, but this
      // is the authoritative computation). Ordinary fodder (rift_brute
      // included) keeps its own authored CSV radius unchanged.
      radius: e.isBoss ? TRUE_BOSS_SCALE * RADIUS_PER_SCALE
        : e.isBeast ? BEAST_SCALE * RADIUS_PER_SCALE : e.radius,
    })),
    characters: raw.characters,
    weapons: ch?.weapon0DamageOverride || ch?.weapon0CooldownMult
      ? raw.weapons.map((w, i) => i === 0 ? {
        ...w,
        ...(ch?.weapon0DamageOverride ? { damagePerHit: ch.weapon0DamageOverride } : {}),
        ...(ch?.weapon0CooldownMult && ch.weapon0CooldownMult > 0
          ? { cooldownTicks: Math.max(1, Math.round(w.cooldownTicks * ch.weapon0CooldownMult)) }
          : {}),
      } : w)
      : raw.weapons,
    upgrades: raw.upgrades,
    waves,
    monoliths: (raw.monoliths ?? [])
      .filter((m) => m.chapter === chapter)
      .slice().sort((a, b) => a.atTick - b.atTick),
    gateGroups,
    dirTable: Float64Array.from(raw.dirTable),
    xpTable: Int32Array.from(raw.xpTable),
    configHash: raw.configHash,
  };
}
